import { getAccessToken } from '@yawi/infrastructure';
import { useCallback, useEffect, useRef, useState } from 'react';

interface AuthenticatedImageProps {
  src: string | undefined;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  /** @default true */
  lazy?: boolean;
}

/**
 * Image component that fetches images with authentication headers.
 * Use this for images served from protected endpoints like v1/cdn/files/
 *
 * When `src` changes (e.g. after replacing a pet/avatar photo) the previous
 * blob is cleared and the new one is (re)fetched. The lazy IntersectionObserver
 * is attached via a callback ref on the placeholder so it works reliably even
 * when the component was already showing a loaded image.
 */
export const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  alt,
  className,
  fallback,
  lazy = true,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  // Holds the latest fetch routine so the lazy observer always triggers the
  // fetch for the current `src`.
  const fetchRef = useRef<(() => void) | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lazyRef = useRef(lazy);
  lazyRef.current = lazy;

  useEffect(() => {
    // Clear any previous object URL so a stale image is never shown.
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setBlobUrl(null);
    setError(false);

    if (!src) {
      setLoading(false);
      fetchRef.current = null;
      return;
    }

    // Reset to the loading state so the placeholder (and its callback ref)
    // re-mounts and the observer can re-attach for the new src.
    setLoading(true);

    let isMounted = true;

    const fetchImage = async () => {
      try {
        const token = getAccessToken();
        const response = await fetch(src, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) throw new Error('Failed to load image');

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        if (isMounted) {
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
          }
          blobUrlRef.current = url;
          setBlobUrl(url);
          setLoading(false);
        } else {
          URL.revokeObjectURL(url);
        }
      } catch {
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchRef.current = fetchImage;

    // Eager fetch when lazy loading is disabled.
    if (!lazy) {
      fetchImage();
    }

    return () => {
      isMounted = false;
      fetchRef.current = null;
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [src, lazy]);

  // Revoke the last object URL on unmount.
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // Callback ref on the placeholder: attaches the IntersectionObserver as soon
  // as the placeholder mounts (including after a src change), so the fetch is
  // triggered reliably for lazy images.
  const placeholderRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    // Non-lazy fetches are triggered eagerly by the effect; only lazy images
    // need an observer attached to the placeholder.
    if (!node || !lazyRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          fetchRef.current?.();
        }
      },
      { threshold: 0.1, rootMargin: '200px' },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  if (!src || error) {
    return <>{fallback || null}</>;
  }

  if (loading) {
    return <div ref={placeholderRef} className={`${className} animate-pulse bg-slate-200`} />;
  }

  return <img src={blobUrl || ''} alt={alt} className={className} loading="lazy" />;
};
